/* eslint-disable no-undef */
import { useEffect, useRef, useCallback } from 'react';

import { useAuth } from '../context/AuthContext';
import { tokenService } from '../services/tokenService';
import { onGaEvent } from '../utils/analytics';

interface TrackedEvent {
  event_type: 'click' | 'form_submit' | 'ga_event';
  url: string;
  button_id: string;
  button_text: string;
  button_parent_class: string;
  form_data?: string;
  ga_event_name?: string;
  ga_params?: string;
  timestamp: string;
}

const WS_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/^http/, 'ws');
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 10;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

const CLICK_TARGET_SELECTOR = [
  'button', 'input[type="button"]', 'input[type="submit"]', 'input[type="reset"]',
  'a[href]', '[role="button"]', '[role="link"]', '[role="menuitem"]', '[role="tab"]',
].join(',');

const findClickable = (t: EventTarget | null): HTMLElement | null => {
  const el = t instanceof Element ? t : t instanceof Node ? t.parentElement : null;
  const control = el?.closest(CLICK_TARGET_SELECTOR);

  return control instanceof HTMLElement ? control : null;
};

const btnText = (el: HTMLElement): string => {
  const labelledBy = el.getAttribute('aria-labelledby')?.split(/\s+/)
    .map((id) => document.getElementById(id)?.textContent || '').join(' ');
  const labels = [
    labelledBy, el.getAttribute('aria-label'),
    el instanceof HTMLInputElement ? el.value : el.innerText ?? el.textContent,
    el.getAttribute('title'), el.querySelector('img[alt]')?.getAttribute('alt'),
    el.querySelector('svg title')?.textContent, el.getAttribute('name'), el.id,
  ];

  return labels.find((label) => label?.trim())?.trim().replace(/\s+/g, ' ').slice(0, 200) || '';
};

const toClickEvt = (el: HTMLElement): TrackedEvent => ({
  event_type: 'click', url: window.location.href,
  button_id: el.id || '', button_text: btnText(el),
  button_parent_class: el.parentElement?.className || '',
  timestamp: new Date().toISOString(),
});

const formFields = (form: HTMLFormElement): string => {
  const data: Record<string, string> = {};

  new FormData(form).forEach((v, k) => {
    if (v instanceof File) return;

    const inp = form.elements.namedItem(k) as HTMLInputElement | null;

    if (inp?.type !== 'password') data[k] = String(v);
  });

  return JSON.stringify(data);
};

const toFormEvt = (form: HTMLFormElement): TrackedEvent => {
  const btn = form.querySelector<HTMLElement>('button[type="submit"], input[type="submit"]');

  return {
    event_type: 'form_submit', url: window.location.href,
    button_id: btn?.id || '', button_text: btn ? btnText(btn) : '',
    button_parent_class: btn?.parentElement?.className || '',
    form_data: formFields(form), timestamp: new Date().toISOString(),
  };
};

// -- Flush hook --

const useFlush = (
  wsRef: React.MutableRefObject<WebSocket | null>,
  bufferRef: React.MutableRefObject<TrackedEvent[]>,
) => {
  const flush = useCallback(() => {
    const ws = wsRef.current;

    if (!bufferRef.current.length || !ws || ws.readyState !== WebSocket.OPEN) return;

    try { ws.send(JSON.stringify(bufferRef.current)); bufferRef.current = []; } catch { /* noop */ }
  }, [wsRef, bufferRef]);

  const push = useCallback((evt: TrackedEvent) => {
    bufferRef.current.push(evt);

    if (bufferRef.current.length >= FLUSH_THRESHOLD) flush();
  }, [bufferRef, flush]);

  return { flush, push };
};

// -- WS lifecycle hook --

const closeWs = (wsRef: React.MutableRefObject<WebSocket | null>) => {
  if (!wsRef.current) return;
  wsRef.current.onopen = null;
  wsRef.current.onclose = null;
  wsRef.current.close();
  wsRef.current = null;
};

const wsCleanup = (
  state: { alive: boolean; timer: ReturnType<typeof setTimeout> | null },
  timer: ReturnType<typeof setInterval>,
  flush: () => void,
  wsRef: React.MutableRefObject<WebSocket | null>,
  bufferRef: React.MutableRefObject<TrackedEvent[]>,
) => {
  state.alive = false;
  clearInterval(timer);

  if (state.timer) clearTimeout(state.timer);

  flush();
  bufferRef.current = [];
  closeWs(wsRef);
};

const AUTH_CLOSE_CODES = new Set([4001, 4003, 1008, 1006]);

const handleWsClose = (
  event: CloseEvent,
  state: { attempt: number; timer: ReturnType<typeof setTimeout> | null; alive: boolean },
  wsRef: React.MutableRefObject<WebSocket | null>,
  doConnect: () => void,
) => {
  if (!state.alive) return;
  wsRef.current = null;

  if (AUTH_CLOSE_CODES.has(event.code)) {
    tokenService.refreshToken()
      .then(() => { if (state.alive) doConnect(); })
      .catch(() => { state.alive = false; });

    return;
  }

  state.attempt += 1;
  state.timer = setTimeout(doConnect, Math.min(RECONNECT_BASE_MS * Math.pow(2, state.attempt), RECONNECT_MAX_MS));
};

const createWsConnector = (
  state: { attempt: number; timer: ReturnType<typeof setTimeout> | null; alive: boolean },
  wsRef: React.MutableRefObject<WebSocket | null>,
  flush: () => void,
) => {
  const doConnect = (): void => {
    if (!state.alive) return;

    const token = tokenService.getAccessToken();

    if (!token) return;

    const ws = new WebSocket(`${WS_URL}/ws/events?token=${token}`);

    ws.onopen = () => { if (!state.alive) return; state.attempt = 0; flush(); };
    ws.onclose = (e: CloseEvent) => handleWsClose(e, state, wsRef, doConnect);
    wsRef.current = ws;
  };

  return doConnect;
};

const useWsLifecycle = (
  wsRef: React.MutableRefObject<WebSocket | null>,
  bufferRef: React.MutableRefObject<TrackedEvent[]>,
  flush: () => void,
  userId: string | undefined,
) => {
  useEffect(() => {
    if (!userId) return undefined;

    const state = { attempt: 0, timer: null as ReturnType<typeof setTimeout> | null, alive: true };
    const doConnect = createWsConnector(state, wsRef, flush);

    doConnect();

    const timer = setInterval(flush, FLUSH_INTERVAL_MS);

    return () => wsCleanup(state, timer, flush, wsRef, bufferRef);
  }, [userId, wsRef, bufferRef, flush]);
};

// -- Click tracker hook --

const clickHandler = (push: (e: TrackedEvent) => void) => (e: MouseEvent) => {
  const el = findClickable(e.target);

  if (el) push(toClickEvt(el));
};

const useClickTracker = (isActive: boolean, push: (e: TrackedEvent) => void) => {
  useEffect(() => {
    if (!isActive) return undefined;

    const handler = clickHandler(push);

    document.addEventListener('click', handler, true);

    return () => document.removeEventListener('click', handler, true);
  }, [isActive, push]);
};

// -- Form tracker hook --

const useFormTracker = (isActive: boolean, push: (e: TrackedEvent) => void) => {
  useEffect(() => {
    if (!isActive) return undefined;

    const handler = (e: Event) => {
      const form = e.target as HTMLFormElement;

      if (form.tagName?.toLowerCase() === 'form') push(toFormEvt(form));
    };

    document.addEventListener('submit', handler, true);

    return () => document.removeEventListener('submit', handler, true);
  }, [isActive, push]);
};

const useGaTracker = (isActive: boolean, push: (e: TrackedEvent) => void) => {
  useEffect(() => {
    if (!isActive) return undefined;

    return onGaEvent((name, params) => {
      push({
        event_type: 'ga_event', url: window.location.href,
        button_id: '', button_text: '', button_parent_class: '',
        ga_event_name: name, ga_params: params ? JSON.stringify(params) : undefined,
        timestamp: new Date().toISOString(),
      });
    });
  }, [isActive, push]);
};

const useTrackers = (isActive: boolean, push: (e: TrackedEvent) => void) => {
  useClickTracker(isActive, push);
  useFormTracker(isActive, push);
  useGaTracker(isActive, push);
};

// -- Main hook --

const useRefs = () => ({
  wsRef: useRef<WebSocket | null>(null),
  bufferRef: useRef<TrackedEvent[]>([]),
});

export const useEventTracker = (): void => {
  const { user, isAuthenticated, isImpersonating } = useAuth();
  const trackingUserId = isAuthenticated && !isImpersonating ? user?.id : undefined;
  const { wsRef, bufferRef } = useRefs();
  const { flush, push } = useFlush(wsRef, bufferRef);

  useWsLifecycle(wsRef, bufferRef, flush, trackingUserId);
  useTrackers(!!trackingUserId, push);
};
